/**
 * 공정분류표(736: 직영, 895: 수급업체) 조회/캐시 유틸
 *
 * 위험성평가 문서(813/814)에는 작성 시점의 공정분류표 스냅샷(body.공정분류.raw)만 남기 때문에,
 * 공정분류표가 개정(리네임)되면 문서에 저장된 이름은 옛 이름이 된다.
 * 그래서 검색 선택지는 문서 저장값이 아니라 공정분류표의 "현재" 데이터를 기준으로 만들어야 한다.
 *
 * 예전에는 계층 레벨(팀 -> 공장 -> 대 -> 중 -> 소)을 한 단계 내려갈 때마다 공정분류표를 다시 조회하고,
 * 좁혀진 후보 id 목록을 IN 필터로 URL 에 실어 보냈다. 후보가 수천 건이 되면 쿼리스트링 길이 제한에
 * 걸려 요청 자체가 실패했다. 여기서는 공정분류표 전체를 한 번만 받아 캐시하고, 단계별 필터링은
 * 모두 메모리에서 계산한다.
 *
 * 캐시는 3단계다.
 *   1) 메모리   : 같은 세션 안에서는 재조회 없음 (CACHE_TTL)
 *   2) IndexedDB: 새로고침/탭 이동에도 유지 (localStorage 는 용량 부족 전력이 있어 사용하지 않는다)
 *   3) 리비전 API: 새로고침 시 "바뀐 게 있는지"만 1회 물어보고, 같으면 목록 조회를 아예 생략한다
 *                 GET /api/v1/board/post/{boardId}/revision
 *
 * 리비전이 같으면 요청 1회(경량), 다르면 그때만 전체 목록을 다시 받는다.
 */

import HttpRequest from "@/util/HttpRequest";
import IndexDbUtil from "@/util/IndexDbUtil";

// 계단식 선택의 상위 -> 하위 순서
export const CLASSIFICATION_LEVELS = ["팀업체명", "공장명", "대분류", "중분류", "소분류"];

// 813 -> 736(공정분류표(직영)), 814 -> 895(공정분류표(수급업체))
export const CLASSIFICATION_BOARD_MAP = { 813: 736, 814: 895 };

// 공정분류표 게시판마다 "팀" 개념의 필드명이 다르다
const TEAM_BODY_KEY = { 736: "팀명", 895: "업체명" };

const CACHE_TTL = 5 * 60 * 1000;
const CHUNK_SIZE = 2000;

// IndexedDB 보관 기간. 신선도는 리비전 비교로 보장하므로 넉넉하게 잡는다.
const IDB_TTL = 7 * 24 * 60 * 60 * 1000;
const IDB_KEY_PREFIX = "classificationTable";

// key: 공정분류표 게시판 id -> { rows, timestamp } 또는 { promise }
// 컴포넌트가 재생성돼도 유지되도록 모듈 스코프에 둔다.
const cache = new Map();

/**
 * IndexedDB 캐시 키.
 * 리비전은 "게시판 + 스코프" 단위라 사용자 권한 변경까지는 감지하지 못하므로,
 * 리비전 API 문서 권고대로 키에 사용자 식별자를 포함한다.
 * (공정분류표 조회는 projectId/fieldId 없이 게시판 전체를 받으므로 그 외 스코프는 키에 넣지 않는다)
 */
function buildCacheKey(store, classificationBoardId) {
  const userInfo = (store && store.state && store.state.auth && store.state.auth.userInfo) || {};
  const userId = userInfo.id;
  if (!userId) return null; // 로그인 정보가 없으면 영구 캐시를 쓰지 않는다
  return `${IDB_KEY_PREFIX}:${userId}:${classificationBoardId}`;
}

/**
 * 게시글 목록 리비전 조회 (경량).
 * 목록 조회와 같은 스코프로 호출해야 하는데, 공정분류표는 스코프 파라미터 없이 게시판 전체를 받으므로
 * 여기서도 파라미터를 붙이지 않는다.
 */
async function loadRevision(classificationBoardId) {
  try {
    const response = await HttpRequest.get(`/api/v1/board/post/${classificationBoardId}/revision`);
    return (response && response.revision) || null;
  } catch (err) {
    // 리비전을 못 가져오면 캐시를 쓰지 않고 전체 조회로 진행한다.
    console.log(`classification revision(${classificationBoardId}) err`, err);
    return null;
  }
}

/**
 * 공정분류표 게시판의 "팀" 필드명 (736: 팀명, 895: 업체명)
 */
export function teamBodyKey(classificationBoardId) {
  return TEAM_BODY_KEY[classificationBoardId] || "팀명";
}

/**
 * 위험성평가 게시판 id 목록(813/814)에 대응하는 공정분류표 게시판 id 목록
 */
export function toClassificationBoardIds(boardIds) {
  const mapped = (boardIds || []).map((id) => CLASSIFICATION_BOARD_MAP[id]).filter(Boolean);
  return [...new Set(mapped)];
}

async function fetchClassificationTable(store, classificationBoardId) {
  const teamKey = teamBodyKey(classificationBoardId);
  const rows = [];
  let page = 1;
  let lastPage = 1;

  do {
    const response = await store.dispatch("board/LOAD_POST_LIST", {
      boardId: classificationBoardId,
      page,
      perPage: CHUNK_SIZE,
      orderTarget: "id",
      orderDirection: "asc",
      withMeta: true,
      onlyMeta: true,
      withoutRelation: true,
      metaKeys: ["body.사업장.field_name", `body.${teamKey}`, "body.공장명", "body.대분류", "body.중분류", "body.소분류"],
    });

    for (const post of (response && response.data) || []) {
      const body = (post.postContents && post.postContents.body) || {};
      rows.push({
        id: Number(post.id),
        classificationBoardId,
        사업장명: (body.사업장 && body.사업장.field_name) || "",
        팀업체명: body[teamKey] || "",
        공장명: body.공장명 || "",
        대분류: body.대분류 || "",
        중분류: body.중분류 || "",
        소분류: body.소분류 || "",
      });
    }

    lastPage = (response && response.last_page) || 1;
    page++;
  } while (page <= lastPage);

  return rows;
}

/**
 * 리비전을 먼저 확인해서, 바뀐 게 없으면 IndexedDB 캐시를 그대로 쓰고 목록 조회를 생략한다.
 */
async function resolveClassificationTable(store, classificationBoardId) {
  const cacheKey = buildCacheKey(store, classificationBoardId);
  const revision = await loadRevision(classificationBoardId);

  if (cacheKey && revision) {
    let cached = null;
    try {
      cached = await IndexDbUtil.getItem(cacheKey);
    } catch (err) {
      cached = null; // 읽기 실패 시에는 캐시가 없는 것으로 본다
    }

    if (cached && cached.revision === revision && Array.isArray(cached.rows)) {
      return cached.rows;
    }
  }

  const rows = await fetchClassificationTable(store, classificationBoardId);

  if (cacheKey && revision) {
    try {
      await IndexDbUtil.setItem(cacheKey, { revision, rows, savedAt: new Date().toISOString() }, IDB_TTL);
    } catch (err) {
      // 저장 실패(용량 초과 등)해도 메모리 캐시로 동작하므로 무시한다.
    }
  }

  return rows;
}

/**
 * 공정분류표 전체를 옵션 계산에 필요한 필드만 추려서 불러온다.
 * onlyMeta 로 metaKeys 에 적힌 값만 받기 때문에 전체 건을 받아도 응답이 가볍다.
 *
 * @param {object} store - vuex store ($store)
 * @param {number} classificationBoardId - 736 또는 895
 * @returns {Promise<Array>} [{ id, 사업장명, 팀업체명, 공장명, 대분류, 중분류, 소분류 }]
 */
export function loadClassificationTable(store, classificationBoardId) {
  const cached = cache.get(classificationBoardId);
  if (cached) {
    if (cached.promise) return cached.promise;
    if (new Date().getTime() - cached.timestamp < CACHE_TTL) return Promise.resolve(cached.rows);
  }

  const promise = resolveClassificationTable(store, classificationBoardId)
    .then((rows) => {
      cache.set(classificationBoardId, { rows, timestamp: new Date().getTime() });
      return rows;
    })
    .catch((err) => {
      console.log("loadClassificationTable err", err);
      cache.delete(classificationBoardId);
      return [];
    });

  cache.set(classificationBoardId, { promise });
  return promise;
}

/**
 * 공정분류표 캐시를 비운다. (메모리 + IndexedDB)
 * 공정분류표를 수정한 직후처럼 즉시 반영이 필요할 때 사용한다.
 */
export async function clearClassificationCache(store, classificationBoardId) {
  if (classificationBoardId) {
    cache.delete(classificationBoardId);
    const cacheKey = buildCacheKey(store, classificationBoardId);
    if (cacheKey) await IndexDbUtil.removeItem(cacheKey).catch(() => {});
    return;
  }

  cache.clear();
  await IndexDbUtil.removeByPrefix(`${IDB_KEY_PREFIX}:`).catch(() => {});
}

/**
 * 여러 공정분류표(736/895)를 한꺼번에 불러와 하나의 배열로 합친다.
 * 815처럼 직영/수급업체를 함께 보는 화면에서 사용한다.
 */
export async function loadClassificationTables(store, classificationBoardIds) {
  const tables = await Promise.all((classificationBoardIds || []).map((boardId) => loadClassificationTable(store, boardId)));
  return tables.flat();
}

/**
 * 캐시된 공정분류표에서 특정 레벨의 선택지를 계산한다. (서버 조회 없음)
 *
 * @param {Array} rows - loadClassificationTable(s) 결과
 * @param {string} level - "팀업체명" | "공장명" | "대분류" | "중분류" | "소분류"
 * @param {object} options
 * @param {string} options.fieldName - 선택된 사업장명 ("전체"면 미적용)
 * @param {Array} options.parentValues - 상위 레벨의 선택값들 (CLASSIFICATION_LEVELS 순서)
 * @returns {Array} ["전체", ...정렬된 값들]
 */
export function buildLevelOptions(rows, level, { fieldName, parentValues = [] } = {}) {
  const depth = CLASSIFICATION_LEVELS.indexOf(level);
  if (depth < 0) return ["전체"];

  const values = new Set();

  for (const row of rows || []) {
    if (fieldName && fieldName !== "전체" && row.사업장명 !== fieldName) continue;

    let matched = true;
    for (let i = 0; i < depth; i++) {
      const parent = parentValues[i];
      if (parent && parent !== "전체" && row[CLASSIFICATION_LEVELS[i]] !== parent) {
        matched = false;
        break;
      }
    }
    if (!matched) continue;

    if (row[level]) values.add(row[level]);
  }

  const sorted = Array.from(values).sort((a, b) => {
    return a > b ? 1 : a < b ? -1 : 0;
  });
  return ["전체", ...sorted];
}
